// ContentAwareG37Gate — Gate G3.7 Phase 6 (plan §8).
//
// Surfaces the SRD Depth Probe's findings as architect decisions:
// every visible finding (filtered by severity per intervention
// budget at emit time) becomes one G37-<id> decision the architect
// answers via Approve / Edit / Defer / Reject.
//
// Layout (plan §8.1):
//
//   ┌────────────────────────────────────────────────────────────┐
//   │ Header (chips, severity counts, RQS preview, probe error)  │
//   ├────────────────────────────────────────────────────────────┤
//   │ DepthPass2Banner  (running / clean / regressed / failed)   │
//   ├────────────────────────────────────────────────────────────┤
//   │ UniversalSmellsPanel  (collapsible, 8 smells)              │
//   ├────────────────────────────────────────────────────────────┤
//   │ Toolbar  (search · severity filter · hide-decided · pages) │
//   ├────────────────────────────────────────────────────────────┤
//   │ DepthFindingCard × N  (one per visible finding)            │
//   ├────────────────────────────────────────────────────────────┤
//   │ Sticky footer  (decided/total · Apply & continue)          │
//   └────────────────────────────────────────────────────────────┘
//
// The wire format the depth_resolver expects on submit
// (``ai_alm.services.depth_resolver._parse_option``):
//
//   approve  →  status: 'approved' , answer: 'approve'
//   edit     →  status: 'edited'   , answer: 'edit:<EARS clause>'
//   defer    →  status: 'skipped'  , answer: 'defer:<reason>'  (+ skip_reason)
//   reject   →  status: 'rejected' , answer: 'reject'
//
// Pass-2 polling: after submit succeeds the panel polls
// ``phase3/depth-findings-pass2.json`` for ~30 s so the closure
// badges on each card refresh in place. Polling stops once the
// envelope lands or the architect navigates away.

import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  CheckCircle2,
  EyeOff,
  Info,
  Loader2,
  PlayCircle,
  RefreshCw,
  Search,
  Sparkles,
} from 'lucide-react'

import { api, ApiError, projectFileUrl } from '@/lib/api'
import { runFolder } from '@/lib/format'
import { toast } from '@/hooks/use-toast'
import type { GateDecisionRecord, Project } from '@/types'
import {
  type ClosureStatus,
  type DepthFindingsPass2,
  G37_OPTION_APPROVE,
  G37_OPTION_DEFER,
  G37_OPTION_EDIT,
  G37_OPTION_PROMOTE,
  G37_OPTION_REJECT,
  type G37DecisionPayload,
  type G37Option,
} from '@/types/depth'
import {
  gateDraftKey,
  reconcileDraftWithGate,
  useGateDraft,
  useGateDraftStore,
} from '@/stores/gateDraftStore'

import { ControlsCoverageFindingsPanel } from './ControlsCoverageFindingsPanel'
import { DataDictionaryFindingsPanel } from './DataDictionaryFindingsPanel'
import { RiskRegisterFindingsPanel } from './RiskRegisterFindingsPanel'
import { DepthFindingCard } from './DepthFindingCard'
import { DepthPass2Banner } from './DepthPass2Banner'
import { DeploymentDecisionsPanel } from './DeploymentDecisionsPanel'
import { ThreatModelFindingsPanel } from './ThreatModelFindingsPanel'
import { UniversalSmellsPanel } from './UniversalSmellsPanel'

// G3.7 is stored numerically as 37; the user-facing label is
// "G3.7" everywhere. Mirrors ``gate_emitter.gate_label(37)``.
const G37_GATE_NUMBER = 37
const PAGE_SIZE = 12
const PASS2_POLL_INTERVAL_MS = 4_000
const PASS2_POLL_MAX_ATTEMPTS = 30 // ~2 min ceiling

type SeverityFilter = 'all' | 'critical' | 'high' | 'medium' | 'low'

export interface ContentAwareG37Props {
  project: Project
  runId: number
  /** Called after the gate is approved so the page can advance focus. */
  onApproved?: () => void
}

// --------------------------------------------------------------------------- //
// Component
// --------------------------------------------------------------------------- //
export function ContentAwareG37Gate({
  project,
  runId,
  onApproved,
}: ContentAwareG37Props) {
  const queryClient = useQueryClient()
  const draftKey = gateDraftKey(project.id, runId, G37_GATE_NUMBER)
  const decisions = useGateDraft(draftKey)
  const setDecisionsDraft = useGateDraftStore((s) => s.setDraft)
  const clearDecisionsDraft = useGateDraftStore((s) => s.clearDraft)
  const setDecisions = (next: GateDecisionRecord[]) =>
    setDecisionsDraft(draftKey, next)

  // Per-decision manual-text buffer. Lifted here so DepthFindingCard
  // remounts (filter / paginate) don't lose what the architect typed.
  const [manualText, setManualText] = useState<Record<string, string>>({})
  const updateManualText = (id: string, value: string) =>
    setManualText((m) => ({ ...m, [id]: value }))

  // Validation feedback from the last failed submit. Populated by
  // the ``submit.onError`` handler so each offending DepthFindingCard
  // can render its diagnostic inline (instead of forcing the
  // architect to hunt through 26 rows looking for the bad one). The
  // keys are decision ids; the values are the resolver's reason strings.
  const [offendingDiagnostics, setOffendingDiagnostics] = useState<
    Record<string, string>
  >({})

  const [searchTerm, setSearchTerm] = useState('')
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('all')
  const [hideDecided, setHideDecided] = useState(false)
  const [page, setPage] = useState(0)

  const gate = useQuery({
    queryKey: ['gate', project.id, runId, G37_GATE_NUMBER],
    queryFn: () => api.getGate(project.id, runId, G37_GATE_NUMBER),
    retry: false,
  })

  // Reconcile the persisted draft against the freshly emitted gate.
  useEffect(() => {
    if (!gate.data) return
    reconcileDraftWithGate(draftKey, gate.data)
  }, [gate.data, draftKey])

  // Pass-2 envelope (post-submit) — polled for closure badges.
  const [pass2Polling, setPass2Polling] = useState(false)
  const [pass2Envelope, setPass2Envelope] =
    useState<DepthFindingsPass2 | null>(null)
  const [pass2FetchError, setPass2FetchError] = useState<string | null>(null)
  // Guards the load-time pass-2 resume (below) to a single attempt so a
  // run whose pass-2 never lands can't re-arm the poll forever after the
  // ceiling. Set whenever polling starts (submit OR resume).
  const pass2ResumeTried = useRef(false)

  // Hydrate pass-2 envelope from disk on mount in case the runner
  // already finished (e.g. reload after submit). Failure is silent —
  // the envelope just stays null and the banner suppresses itself.
  useEffect(() => {
    let cancelled = false
    const url = projectFileUrl(
      project.id,
      `${runFolder(runId)}/phase3/depth-findings-pass2.json`,
    )
    fetch(url)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled) return
        if (data) setPass2Envelope(data as DepthFindingsPass2)
      })
      .catch(() => {
        /* file 404 is the empty state; nothing to surface */
      })
    return () => {
      cancelled = true
    }
  }, [project.id, runId])

  // Resume pass-2 polling on load when the gate is already resolved but
  // the pass-2 envelope hasn't landed on disk yet — that means pass 2 is
  // still verifying in the background (a reload mid-verification). We
  // gate on ``pass2ResumeTried`` so this fires at most once: if pass 2
  // genuinely never produces a file, the poll ceiling stops it and we
  // don't re-arm. Without this, a reload would enable Continue while
  // pass 2 was still running.
  const gateResolved =
    Boolean(gate.data?.status) && gate.data?.status !== 'pending'
  useEffect(() => {
    if (
      gateResolved &&
      !pass2Envelope &&
      !pass2Polling &&
      !pass2ResumeTried.current
    ) {
      pass2ResumeTried.current = true
      setPass2Polling(true)
    }
  }, [gateResolved, pass2Envelope, pass2Polling])

  // When pass-2 is running, poll the envelope file every 4 s until
  // it lands or we hit the attempt ceiling.
  useEffect(() => {
    if (!pass2Polling) return
    let attempts = 0
    const url = projectFileUrl(
      project.id,
      `${runFolder(runId)}/phase3/depth-findings-pass2.json`,
    )
    const tick = async () => {
      attempts += 1
      try {
        const r = await fetch(url, { cache: 'no-store' })
        if (r.ok) {
          const env = (await r.json()) as DepthFindingsPass2
          setPass2Envelope(env)
          setPass2Polling(false)
          return
        }
      } catch (err) {
        setPass2FetchError(
          err instanceof Error ? err.message : 'pass-2 fetch failed',
        )
      }
      if (attempts >= PASS2_POLL_MAX_ATTEMPTS) {
        setPass2Polling(false)
      }
    }
    const handle = window.setInterval(tick, PASS2_POLL_INTERVAL_MS)
    return () => window.clearInterval(handle)
  }, [pass2Polling, project.id, runId])

  // ----- Mutations ------------------------------------------------- //
  const emit = useMutation({
    mutationFn: () => api.createGate(project.id, runId, G37_GATE_NUMBER),
    onSuccess: () => {
      clearDecisionsDraft(draftKey)
      gate.refetch()
      toast({ variant: 'success', title: 'G3.7 re-emitted' })
    },
    onError: (err) =>
      toast({
        variant: 'destructive',
        title: 'Re-emit failed',
        description: (err as Error).message,
      }),
  })

  const submit = useMutation({
    mutationFn: () => {
      // Clear any stale validation feedback before we re-submit;
      // we'll repopulate from the next onError if it 422s again.
      setOffendingDiagnostics({})
      return api.submitGateDecisions(
        project.id,
        runId,
        G37_GATE_NUMBER,
        toWireDecisions(decisions, manualText),
      )
    },
    onSuccess: (g) => {
      gate.refetch()
      queryClient.invalidateQueries({
        queryKey: ['artifact-index', project.id, runId],
      })
      // Kick off pass-2 polling — the resolver fires pass-2
      // asynchronously per plan §7.4. We poll the artifact file
      // until it lands.
      pass2ResumeTried.current = true
      setPass2Polling(true)
      setPass2Envelope(null)
      setPass2FetchError(null)
      // Build a status-aware confirmation. The aggregate gate.status
      // rolls up to ``rejected`` whenever any single decision is a
      // Reject and none are Edits — even if most findings were
      // Approved. That's a normal G3.7 outcome (Reject is the
      // false-positive path), so we treat every non-error post-submit
      // status as success and surface a count summary so the
      // architect can tell the submit landed.
      const tally = decisions.reduce(
        (acc, d) => {
          if (d.status === 'approved') acc.approved += 1
          else if (d.status === 'edited') acc.edited += 1
          else if (d.status === 'rejected') acc.rejected += 1
          else if (d.status === 'skipped') {
            // Defer and Promote-to-DU both flow as ``skipped`` on
            // the gate decision; the option id parsed from the
            // answer disambiguates them for the tally so the
            // architect sees the right shape of work done.
            const answer = (d.answer ?? '').trim()
            const isPromote = answer.startsWith(`${G37_OPTION_PROMOTE}:`)
              || answer === G37_OPTION_PROMOTE
            if (isPromote) acc.promoted += 1
            else acc.deferred += 1
          }
          return acc
        },
        { approved: 0, edited: 0, deferred: 0, rejected: 0, promoted: 0 },
      )
      const parts: string[] = []
      if (tally.approved) parts.push(`${tally.approved} approved`)
      if (tally.edited) parts.push(`${tally.edited} edited`)
      if (tally.deferred) parts.push(`${tally.deferred} deferred`)
      if (tally.rejected) parts.push(`${tally.rejected} rejected`)
      if (tally.promoted) {
        parts.push(
          `${tally.promoted} promoted to Deferred Unknown${tally.promoted === 1 ? '' : 's'}`
        )
      }
      const summary = parts.join(' · ') || 'no decisions'
      const title =
        g.status === 'approved'
          ? 'G3.7 approved'
          : g.status === 'modified'
            ? 'G3.7 resolved with edits'
            : 'G3.7 resolutions applied'
      toast({
        variant: 'success',
        title,
        description: `${summary}. Pass 2 is verifying closures in the background.`,
      })
      // NOTE: do NOT auto-advance here. Advancing to the next step is a
      // separate "Continue" click (below) — same two-step pattern as
      // G2/G3/G4. Calling onApproved() straight from onSuccess unmounts
      // this panel while ``gate.refetch()`` above is still in flight,
      // orphaning it; the parent's shared gate cache then stays
      // ``pending`` and the flow wall never clears off G3.7 (P4 stays
      // "not this phase's turn"). Letting the architect click Continue
      // once the panel shows the resolved state guarantees the cache is
      // fresh before focus moves.
    },
    onError: (err) => {
      // 2026-07-03 UX fix — the submit can "fail" on the client while
      // having fully landed on the server (proxy timeout / dropped
      // connection while pass-2 ran inline pre-fix, or any transient
      // network blip). Refetch the gate so the panel reflects the
      // server's actual state; if the decisions DID persist, the
      // resolved banner + Continue button appear without forcing the
      // architect to click Apply a second time.
      gate.refetch()
      // The depth_resolver returns 422 with ``offending_decision_ids``
      // + ``diagnostics_by_id`` on the error envelope when validation
      // fails. The shared error middleware nests both inside
      // ``error.details`` (api/errors.py), so we read from
      // ``err.detail.details``; the bare top-level keys are dropped
      // before they reach the client.
      const { offendingIds, diagnostics } = readG37ErrorDetails(err)
      setOffendingDiagnostics(diagnostics)
      let offendingMsg = ''
      if (offendingIds.length > 0) {
        offendingMsg = ` (${offendingIds.length} decision${
          offendingIds.length === 1 ? '' : 's'
        }: ${offendingIds.slice(0, 3).join(', ')}${offendingIds.length > 3 ? '…' : ''})`
      }
      // Pull one or two diagnostic snippets into the toast so the
      // user sees *why* a row failed without expanding the card —
      // the most common failure is "promote_to_du requires a
      // non-empty stakeholder" and surfacing it here closes the
      // loop on a five-minute wild-goose chase.
      let diagnosticTail = ''
      const diagEntries = Object.entries(diagnostics)
      if (diagEntries.length > 0) {
        const previews = diagEntries
          .slice(0, 2)
          .map(([id, reason]) => `${id}: ${reason}`)
        diagnosticTail = ` — ${previews.join('; ')}${
          diagEntries.length > 2 ? '…' : ''
        }`
      }
      toast({
        variant: 'destructive',
        title: 'Could not submit decisions',
        description: `${(err as Error).message}${offendingMsg}${diagnosticTail}`,
      })
    },

  })

  // ----- Derived state -------------------------------------------- //
  const counts = useMemo(() => {
    let critical = 0
    let high = 0
    let medium = 0
    let low = 0
    let decided = 0
    for (const d of decisions) {
      const payload = d.payload as G37DecisionPayload | undefined
      const sev = (payload?.severity ?? '').toLowerCase()
      if (sev === 'critical') critical += 1
      else if (sev === 'high') high += 1
      else if (sev === 'medium') medium += 1
      else if (sev === 'low') low += 1
      if (d.status !== 'pending') decided += 1
    }
    return {
      total: decisions.length,
      decided,
      critical,
      high,
      medium,
      low,
    }
  }, [decisions])

  const filteredDecisions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    return decisions.filter((d) => {
      const payload = d.payload as G37DecisionPayload | undefined
      if (hideDecided && d.status !== 'pending') return false
      // Placeholder / non-finding decisions (e.g. the confirm-and-
      // advance row ``build_g37_content`` emits when the probe walked
      // the rubric clean OR errored out) carry no payload. They are
      // still real architect decisions and must reach the renderer
      // below, which has a dedicated ``payload.kind !== 'depth_finding'``
      // branch. Bail early so we don't try to filter on severity /
      // search fields that don't exist on this row.
      if (!payload || payload.kind !== 'depth_finding') {
        if (severityFilter !== 'all') return false
        if (term) return false
        return true
      }
      if (severityFilter !== 'all') {
        if ((payload.severity ?? '').toLowerCase() !== severityFilter) {
          return false
        }
      }
      if (!term) return true
      const haystack = [
        payload.finding_id,
        payload.title,
        payload.evidence ?? '',
        payload.expected ?? '',
        payload.category ?? '',
        ...(payload.linked_srs ?? []),
      ]
        .join(' ')
        .toLowerCase()
      return haystack.includes(term)
    })
  }, [decisions, searchTerm, severityFilter, hideDecided])

  // Sort decisions by severity descending so the architect lands on
  // the most-impactful finding first. Ties resolved by id for a
  // deterministic layout.
  const orderedDecisions = useMemo(() => {
    const sevRank: Record<string, number> = {
      critical: 0,
      high: 1,
      medium: 2,
      low: 3,
    }
    return [...filteredDecisions].sort((a, b) => {
      const pa = a.payload as G37DecisionPayload | undefined
      const pb = b.payload as G37DecisionPayload | undefined
      const ra = sevRank[(pa?.severity ?? '').toLowerCase()] ?? 9
      const rb = sevRank[(pb?.severity ?? '').toLowerCase()] ?? 9
      if (ra !== rb) return ra - rb
      return a.id.localeCompare(b.id)
    })
  }, [filteredDecisions])

  const totalPages = Math.max(
    1,
    Math.ceil(orderedDecisions.length / PAGE_SIZE),
  )
  const safePage = Math.min(page, totalPages - 1)
  const paginated = orderedDecisions.slice(
    safePage * PAGE_SIZE,
    safePage * PAGE_SIZE + PAGE_SIZE,
  )

  useEffect(() => {
    setPage(0)
  }, [searchTerm, severityFilter, hideDecided])

  // Pass-2 closure status lookup (id → status) for the per-card badges.
  const closureLookup = useMemo(() => {
    const out: Record<string, ClosureStatus> = {}
    for (const f of pass2Envelope?.findings ?? []) {
      out[f.id] = f.closure_status
    }
    return out
  }, [pass2Envelope])

  // Probe envelope summary fields — the build_g37_content emit puts
  // rubric / shape / probe-error into the gate summary as Markdown,
  // but we also stash a few in the first decision's payload so we
  // can render them as chips without parsing the summary text.
  const firstPayload = decisions.find(
    (d) => (d.payload as G37DecisionPayload | undefined)?.kind === 'depth_finding',
  )?.payload as G37DecisionPayload | undefined
  const rubricId = firstPayload?.rubric_id ?? null
  const shape = firstPayload?.shape ?? null

  const allDecided =
    decisions.length > 0 && decisions.every((d) => d.status !== 'pending')

  // ----- Empty / error states ------------------------------------- //
  const missing = gate.error instanceof ApiError && gate.error.status === 404
  const notVisible =
    gate.error instanceof ApiError && gate.error.code === 'gate.not_visible'

  // Probe error is encoded in the gate summary by the emit; we sniff
  // it from the prefix the builder writes ("⚠️ **Probe error:**").
  const probeError = useMemo(() => {
    const summary = (gate.data as { summary?: string } | undefined)?.summary
    if (!summary) return null
    const m = summary.match(/Probe error:\*\*\s*`([^`]+)`/)
    return m ? m[1] : null
  }, [gate.data])

  // Universal smells live on the gate summary today (build_g37_content
  // appends them as Markdown). For the panel we render them as a
  // structured table — but the inline payload doesn't carry them.
  // Rather than reparsing the summary, we pull the smells from the
  // depth-findings.json envelope on disk (best effort).
  const [smells, setSmells] = useState<
    import('@/types/depth').UniversalSmellOccurrence[] | null
  >(null)
  useEffect(() => {
    let cancelled = false
    const url = projectFileUrl(
      project.id,
      `${runFolder(runId)}/phase3/depth-findings.json`,
    )
    fetch(url)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return
        const arr = (data as { universal_smells?: unknown }).universal_smells
        if (Array.isArray(arr)) {
          setSmells(arr as import('@/types/depth').UniversalSmellOccurrence[])
        }
      })
      .catch(() => {
        /* envelope absent → no panel */
      })
    return () => {
      cancelled = true
    }
  }, [project.id, runId])

  // ----- Render --------------------------------------------------- //
  return (
    <div className="alm-fadein">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="alm-chip alm-chip-warn">
              <AlertCircle className="h-3 w-3" /> Needs you
            </span>
            <span className="alm-chip">Gate G3.7</span>
            {rubricId && (
              <span className="alm-chip alm-chip-info">
                rubric · {rubricId}
              </span>
            )}
            {shape && <span className="alm-chip">shape · {shape}</span>}
            {counts.critical > 0 && (
              <span className="alm-chip alm-chip-warn">
                {counts.critical} Critical
              </span>
            )}
            {counts.high > 0 && (
              <span className="alm-chip alm-chip-warn">
                {counts.high} High
              </span>
            )}
            {counts.medium > 0 && (
              <span className="alm-chip">{counts.medium} Medium</span>
            )}
            {counts.low > 0 && (
              <span className="alm-chip">{counts.low} Low</span>
            )}
            {counts.total > 0 && (
              <span
                className={[
                  'alm-chip',
                  counts.decided === counts.total
                    ? 'alm-chip-info'
                    : 'alm-chip-warn',
                ].join(' ')}
              >
                {counts.decided} / {counts.total} decided
              </span>
            )}
            {gate.data?.status && (
              <span className="alm-chip">{gate.data.status}</span>
            )}
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-100">
            Technical Depth Audit
          </h2>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">
            The SRD Depth Probe walked the per-shape rubric and surfaced
            findings that need your judgement before the Critic runs at G4.
            Approve to apply the probe's clause; Edit to write your own;
            Defer to record an out-of-scope reason; Reject to feed back
            a false positive into the rubric.
          </p>
        </div>
        <div className="flex flex-col gap-1.5 shrink-0">
          <button
            type="button"
            onClick={() => emit.mutate()}
            disabled={emit.isPending}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 px-2.5 py-1 text-xs text-slate-300 hover:bg-white/5 disabled:opacity-50"
          >
            {emit.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <RefreshCw className="h-3 w-3" />
            )}{' '}
            Re-emit
          </button>
        </div>
      </div>

      {notVisible && (
        <div className="rounded-md border border-amber-700/60 bg-amber-950/20 p-4 mb-4 text-sm text-amber-200/80">
          <p className="font-semibold">Gate skipped</p>
          <p className="mt-1">
            G3.7 isn't visible at the current intervention budget. The
            SRD Depth Probe still ran on disk; switch to Standard or
            Thorough to surface findings.
          </p>
        </div>
      )}

      {missing && !notVisible && (
        <div className="rounded-md border border-slate-700/60 bg-slate-900/60 p-4 mb-4 text-sm text-slate-300">
          <p className="font-semibold">No gate yet</p>
          <p className="text-xs text-slate-400 mt-1">
            Click <strong>Re-emit</strong> above to build the G3.7
            artifact from <code>phase3/depth-findings.json</code>.
          </p>
        </div>
      )}

      {gate.isLoading && (
        <div className="flex items-center gap-2 text-sm text-slate-400 mb-4">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading gate…
        </div>
      )}

      {!missing && !notVisible && (
        <>
          {/* Why-this-gate explainer (mirrors G3 / G3.5 hygiene). */}
          <div className="rounded-lg border border-slate-700/60 bg-slate-950/40 p-3 mb-4 flex gap-2 text-xs text-slate-400">
            <Info className="h-4 w-4 text-indigo-400 shrink-0 mt-0.5" />
            <div>
              <strong className="text-slate-200">
                Why this gate exists:
              </strong>{' '}
              the SRD Depth Probe walks the rubric for your project shape
              (e.g. <code>iot_firmware</code> covers watchdog / OTA /
              brown-out, <code>web_saas</code> covers tenancy / RLS /
              auditing) and finds gaps a generic Critic would miss.
              Each finding ships with a probe-proposed EARS clause
              already validated by the deterministic grammar guard —
              one click applies it. Catching depth gaps here is cheap;
              catching them at G4 forces a Phase-3 re-run.
            </div>
          </div>

          {/* Probe error banner — emit-time error from the runner. */}
          {probeError && (
            <div className="rounded-lg border border-amber-700/40 bg-amber-950/20 p-3 mb-4 flex items-start gap-2 text-sm text-amber-100">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-amber-300" />
              <div className="min-w-0">
                <p className="font-semibold">Probe ran with errors</p>
                <p className="text-[11.5px] text-amber-200/80 mt-0.5 leading-snug">
                  <code className="font-mono text-[11px] text-amber-100">
                    {probeError}
                  </code>
                  . The findings list may be incomplete. Click <em>Re-emit</em>{' '}
                  to attempt a fresh probe run, or skip this gate with a
                  recorded reason via the Skip panel above.
                </p>
              </div>
            </div>
          )}

          {/* Pass-2 banner — running / clean / regressed / failed. */}
          <DepthPass2Banner
            running={pass2Polling}
            envelope={pass2Envelope}
            fetchError={pass2FetchError}
          />

          {/* Universal smells — collapsible, top of panel. */}
          {smells && smells.length > 0 && (
            <UniversalSmellsPanel smells={smells} defaultCollapsed />
          )}

          {/* Phase 2 Day 3 — Deployment Decisions table. The panel
              owns its own section chrome (header + source caption) and
              renders nothing at all when the optional decision-resolver
              feature is disabled, so we don't leave an empty
              "Deployment Decisions" heading above an operator-only
              message the end user can't act on (UAT 2026-06-13). */}
          <DeploymentDecisionsPanel
            projectId={project.id}
            runId={runId}
          />


          {/* Phase 4 — Risk Register Floor finding (Layer 2, 2026-07-03).
              Surfaces PEG-RISK-REGISTER-FLOOR-001 so a below-floor
              register is adjudicated here (add rows / confirm-complete-
              with-rationale) instead of hard-failing at Phase-5 export. */}
          <div className="mt-4 rounded-lg border border-slate-700/60 bg-slate-950/40 p-3">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-200">
                Risk Register Coverage
              </h3>
              <span className="text-xs text-slate-500">
                phase3/risk-register.csv
              </span>
            </div>
            <RiskRegisterFindingsPanel projectId={project.id} runId={runId} />
          </div>

          {/* Phase 4 backbone — Data Dictionary Findings.
              Surfaces PEG-DATA-DICT-COMPLETENESS-001 resolver output
              so the architect sees regulatory mandate misses + TBD
              retention defects before G4. Read-only for the backbone;
              the architect remediates by editing data-dictionary.json
              or adding a waiver. */}
          <div className="mt-4 rounded-lg border border-slate-700/60 bg-slate-950/40 p-3">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-200">
                Data Dictionary Findings
              </h3>
              <span className="text-xs text-slate-500">
                phase3/data-dictionary.json
              </span>
            </div>
            <DataDictionaryFindingsPanel
              projectId={project.id}
              runId={runId}
            />
          </div>

          {/* Phase 4 — Threat Model Findings.
              Surfaces PEG-THREAT-MODEL-COMPLETENESS-001 resolver
              output: mandate-asset misses (CDE / SAD for PCI; ePHI /
              Audit Trail for HIPAA; Session cookie / CSRF for
              web_saas) and per-asset STRIDE coverage gaps. Read-only;
              architect remediates by editing threat-model.md or
              adding a waiver. */}
          <div className="mt-4 rounded-lg border border-slate-700/60 bg-slate-950/40 p-3">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-200">
                Threat Model Findings
              </h3>
              <span className="text-xs text-slate-500">
                phase3/threat-model.md
              </span>
            </div>
            <ThreatModelFindingsPanel
              projectId={project.id}
              runId={runId}
            />
          </div>

          {/* Phase 4 — Controls Coverage Findings.
              Surfaces PEG-CONTROLS-COVERAGE-COMPLETENESS-001 resolver
              output: profile-declared regulator clauses (PCI Req. 3 /
              6 / 10; HIPAA §164.308 / §164.312 / §164.530(j)) must
              be cited in compliance-risks.md. Read-only; architect
              remediates by editing the document or adding a waiver. */}
          <div className="mt-4 rounded-lg border border-slate-700/60 bg-slate-950/40 p-3">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-200">
                Controls Coverage Findings
              </h3>
              <span className="text-xs text-slate-500">
                phase3/compliance-risks.md
              </span>
            </div>
            <ControlsCoverageFindingsPanel
              projectId={project.id}
              runId={runId}
            />
          </div>

          {/* Toolbar — only render with non-trivial volume. */}
          {decisions.length > 4 && (
            <div className="flex flex-wrap items-center gap-2 mb-3 rounded-lg border border-slate-700/60 bg-slate-950/40 p-2">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search id, title, evidence, linked SR…"
                  className="w-full bg-slate-900/40 border border-slate-700 rounded-md pl-7 pr-2 py-1 text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-indigo-500/50"
                />
              </div>
              <div className="flex items-center gap-1 text-[11px]">
                <span className="text-slate-500 mr-1">Severity:</span>
                {(['all', 'critical', 'high', 'medium', 'low'] as SeverityFilter[]).map(
                  (sev) => (
                    <button
                      key={sev}
                      type="button"
                      onClick={() => setSeverityFilter(sev)}
                      className={[
                        'rounded-md border px-2 py-1 capitalize',
                        severityFilter === sev
                          ? 'border-indigo-500/60 bg-indigo-950/30 text-indigo-200'
                          : 'border-slate-700 text-slate-300 hover:bg-white/5',
                      ].join(' ')}
                    >
                      {sev}
                    </button>
                  ),
                )}
              </div>
              <button
                type="button"
                onClick={() => setHideDecided((v) => !v)}
                className={[
                  'inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs',
                  hideDecided
                    ? 'border-emerald-500/60 bg-emerald-950/30 text-emerald-200'
                    : 'border-slate-700 text-slate-300 hover:bg-white/5',
                ].join(' ')}
                title="Hide cards you've already decided so you can focus on what's left"
              >
                <EyeOff className="h-3 w-3" />{' '}
                {hideDecided ? 'Hiding decided' : 'Hide decided'}
              </button>
              {orderedDecisions.length === 0 && (
                <span className="text-[11px] text-slate-500 italic">
                  No matches for the current filters.
                </span>
              )}
            </div>
          )}

          {/* Decision cards. */}
          <div className="space-y-3 mb-4">
            {paginated.length === 0 ? (
              <div className="rounded-md border border-slate-700/40 bg-slate-900/30 p-4 text-sm text-slate-400">
                {decisions.length === 0
                  ? 'No depth findings to review at this severity threshold. The probe walked the rubric clean.'
                  : 'No decisions match the current filters.'}
              </div>
            ) : (
              paginated.map((decision) => {
                const payload = decision.payload as
                  | G37DecisionPayload
                  | undefined
                if (!payload || payload.kind !== 'depth_finding') {
                  // Confirm-and-advance row when the probe found nothing.
                  return (
                    <div key={decision.id} className="alm-card">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-[10.5px] text-slate-500">
                            {decision.id}
                          </div>
                          <div className="text-sm text-slate-200 mt-0.5">
                            {decision.question}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setDecisions(
                              decisions.map((d) =>
                                d.id === decision.id
                                  ? {
                                      ...d,
                                      status: 'approved' as const,
                                      answer: 'approve',
                                    }
                                  : d,
                              ),
                            )
                          }}
                          className="alm-decision shrink-0"
                          data-selected={
                            decision.status === 'approved'
                              ? 'approved'
                              : undefined
                          }
                        >
                          <Sparkles className="h-3 w-3" /> Confirm
                        </button>
                      </div>
                    </div>
                  )
                }
                return (
                  <DepthFindingCard
                    key={decision.id}
                    decision={decision}
                    payload={payload}
                    pass2Status={
                      closureLookup[payload.finding_id] ?? null
                    }
                    submissionError={offendingDiagnostics[decision.id] ?? null}
                    manualText={manualText[decision.id] ?? ''}
                    setManualText={(value) =>
                      updateManualText(decision.id, value)
                    }
                    onAnswer={({ option, text }) => {
                      const next = applyOptionToDecision(
                        decision,
                        option,
                        text,
                      )
                      setDecisions(
                        decisions.map((d) =>
                          d.id === decision.id ? next : d,
                        ),
                      )
                    }}
                  />
                )
              })
            )}
          </div>

          {/* Pagination footer (only when needed). */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mb-4 text-xs text-slate-400">
              <div>
                Showing{' '}
                <span className="text-slate-200 font-mono">
                  {safePage * PAGE_SIZE + 1}
                </span>
                –
                <span className="text-slate-200 font-mono">
                  {Math.min(
                    (safePage + 1) * PAGE_SIZE,
                    orderedDecisions.length,
                  )}
                </span>{' '}
                of{' '}
                <span className="text-slate-200 font-mono">
                  {orderedDecisions.length}
                </span>{' '}
                finding{orderedDecisions.length === 1 ? '' : 's'}
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={safePage === 0}
                  className="rounded-md border border-slate-700 px-2 py-1 hover:bg-white/5 disabled:opacity-40"
                >
                  Prev
                </button>
                <span className="px-2 font-mono text-slate-200">
                  {safePage + 1} / {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setPage((p) => Math.min(totalPages - 1, p + 1))
                  }
                  disabled={safePage >= totalPages - 1}
                  className="rounded-md border border-slate-700 px-2 py-1 hover:bg-white/5 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {/* Terminal-status confirmation banner. The aggregate
              gate.status rolls up to ``rejected`` whenever any
              decision is a Reject and none are Edits, even when most
              findings were Approved — so we treat every non-pending
              status as "resolutions applied" and surface a tally the
              architect can see at a glance. */}
          {gate.data?.status &&
            gate.data.status !== 'pending' && (
              <div className="rounded-lg p-4 mb-4 border border-emerald-700/40 bg-emerald-950/20 flex items-start gap-2 text-emerald-200 text-sm">
                <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <div className="font-semibold">
                    {gate.data.status === 'approved'
                      ? 'G3.7 approved'
                      : gate.data.status === 'modified'
                        ? 'G3.7 resolved with edits'
                        : 'G3.7 resolutions applied'}
                  </div>
                  <div className="text-xs text-emerald-300/80 mt-0.5">
                    {[
                      counts.total > 0
                        ? `${counts.decided}/${counts.total} decisions saved`
                        : null,
                      'patches landed on `srd.md`',
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                    {pass2Polling ? (
                      <>
                        {' · '}
                        <span className="text-amber-300">
                          Pass 2 is verifying closures — wait for it to
                          finish before continuing.
                        </span>
                      </>
                    ) : pass2Envelope ? (
                      <> · Pass 2 complete. You can proceed to Phase 4.</>
                    ) : (
                      <>. You can proceed to Phase 4.</>
                    )}
                  </div>
                </div>
              </div>
            )}

          {/* Primary actions */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid="gate-37-submit"
              onClick={() => submit.mutate()}
              disabled={!allDecided || submit.isPending}
              className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-b from-indigo-500 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-500/20 disabled:opacity-50 disabled:cursor-not-allowed hover:from-indigo-400 hover:to-indigo-500"
              title="Apply patches + record deferrals/rejections, then advance"
            >
              {submit.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Applying…
                </>
              ) : gate.data?.status && gate.data.status !== 'pending' ? (
                <>
                  <RefreshCw className="h-4 w-4" /> Re-apply decisions
                </>
              ) : (
                <>
                  <PlayCircle className="h-4 w-4" /> Apply decisions
                </>
              )}
            </button>
            {/* Advance is a SEPARATE step (mirrors G2/G3/G4): once the
                gate is resolved, Continue navigates to the next step.
                Gates never run a phase — Phase 4 runs from the Pipeline
                Runner once its turn comes. */}
            {gate.data?.status && gate.data.status !== 'pending' && (
              <button
                type="button"
                onClick={() => onApproved?.()}
                disabled={pass2Polling}
                title={
                  pass2Polling
                    ? 'Pass 2 is still verifying closures — wait for it to finish'
                    : undefined
                }
                className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-b from-indigo-500 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-500/20 disabled:opacity-50 disabled:cursor-not-allowed hover:from-indigo-400 hover:to-indigo-500"
              >
                {pass2Polling ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Verifying…
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4" /> Continue
                  </>
                )}
              </button>
            )}
            {!allDecided && counts.total > 0 && (
              <span className="text-[11px] text-slate-500 italic">
                Pick an option for every finding above to enable Apply (
                {counts.total - counts.decided} pending).
              </span>
            )}
          </div>
        </>
      )}
    </div>
  )
}

// --------------------------------------------------------------------------- //
// Submission-error envelope reader
// --------------------------------------------------------------------------- //
/**
 * Pull ``offending_decision_ids`` + ``diagnostics_by_id`` off the
 * G3.7 422 envelope. The canonical path is ``error.details.*`` —
 * the shared error middleware (api/errors.py) forwards the resolver's
 * ``to_http_detail().details`` block and drops every top-level key
 * besides ``code`` / ``message`` / ``trace_id``. We also peek at the
 * top-level fields as a back-compat fallback for any future error
 * shape that flattens the envelope.
 */
export function readG37ErrorDetails(err: unknown): {
  offendingIds: string[]
  diagnostics: Record<string, string>
} {
  if (!(err instanceof ApiError)) {
    return { offendingIds: [], diagnostics: {} }
  }
  const detail = (err.detail ?? {}) as Record<string, unknown>
  const nested = (detail.details ?? {}) as Record<string, unknown>
  const rawIds =
    nested.offending_decision_ids ?? detail.offending_decision_ids
  const rawDiag =
    nested.diagnostics_by_id ?? detail.diagnostics_by_id
  const offendingIds: string[] = Array.isArray(rawIds)
    ? (rawIds as unknown[]).filter((v): v is string => typeof v === 'string')
    : []
  const diagnostics: Record<string, string> = {}
  if (rawDiag && typeof rawDiag === 'object' && !Array.isArray(rawDiag)) {
    for (const [k, v] of Object.entries(rawDiag as Record<string, unknown>)) {
      if (typeof v === 'string') diagnostics[k] = v
    }
  }
  return { offendingIds, diagnostics }
}

// --------------------------------------------------------------------------- //
// Decision wire helpers
// --------------------------------------------------------------------------- //
/**
 * Apply an architect option pick to a gate decision, producing a
 * fresh GateDecisionRecord with the right ``status`` + ``answer``
 * combination for the depth_resolver wire format.
 *
 *   approve  →  status: 'approved' , answer: 'approve'
 *   edit     →  status: 'edited'   , answer: 'edit:<EARS clause>'
 *   defer    →  status: 'skipped'  , answer: 'defer:<reason>'  + skip_reason
 *   reject   →  status: 'rejected' , answer: 'reject'
 *
 * The ``maps_to_status`` field on the option is the source of truth
 * — the resolver also accepts the colon-separated short form, so we
 * stay forwards-compatible with future option layouts that share
 * the same status mapping.
 */
function applyOptionToDecision(
  decision: GateDecisionRecord,
  option: G37Option,
  text: string | null,
): GateDecisionRecord {
  const status =
    option.maps_to_status ?? statusForOptionId(option.id) ?? decision.status
  let answer: string
  if (option.needs_text) {
    answer = text ? `${option.id}:${text}` : option.id
  } else {
    answer = option.id
  }
  const next: GateDecisionRecord = {
    ...decision,
    status,
    answer,
  }
  // Defer pick — also surface the reason on ``skip_reason`` so the
  // legacy SkipGate plumbing + audit ledger captures it without
  // re-parsing the answer string.
  if (option.id === G37_OPTION_DEFER || status === 'skipped') {
    next.skip_reason = text ?? decision.skip_reason ?? null
  } else {
    next.skip_reason = null
  }
  // Reject pick — copy the rejection text into ``rejection_reason``
  // so the gate's existing rejection-reason renderer (history.log,
  // audit ledger) sees it.
  if (option.id === G37_OPTION_REJECT) {
    next.rejection_reason = text ?? decision.rejection_reason ?? null
  } else if (option.id === G37_OPTION_APPROVE) {
    next.rejection_reason = null
  }
  return next
}

function statusForOptionId(
  optionId: string,
): GateDecisionRecord['status'] | null {
  switch (optionId) {
    case G37_OPTION_APPROVE:
      return 'approved'
    case G37_OPTION_EDIT:
      return 'edited'
    case G37_OPTION_DEFER:
      return 'skipped'
    case G37_OPTION_REJECT:
      return 'rejected'
    case G37_OPTION_PROMOTE:
      // Promote-to-DU maps to ``skipped`` on the gate-decision
      // status (no SRD edit, no rejection), same as Defer. The
      // resolver dispatches on the ``promote_to_du`` option id
      // parsed from the answer field to write the
      // ``depth-deferred-unknowns.json`` ledger.
      return 'skipped'
    default:
      return null
  }
}

/**
 * Massage the in-memory draft into the wire shape the API expects.
 * The depth_resolver accepts both the structured ``answer`` form
 * (``edit:<text>`` / ``defer:<reason>``) and the legacy status-only
 * pre-G3.7 form. We always submit the structured form so the
 * resolver can disambiguate Edit-with-empty-clause from Approve.
 */
function toWireDecisions(
  decisions: GateDecisionRecord[],
  manualText: Record<string, string>,
): GateDecisionRecord[] {
  return decisions.map((d) => {
    const payload = d.payload as G37DecisionPayload | undefined
    if (!payload) return d
    const answer = (d.answer ?? '').trim()
    if (!answer) return d
    const optionId = answer.includes(':')
      ? answer.slice(0, answer.indexOf(':'))
      : answer
    // Re-attach the buffered manual text on submit — useful when the
    // architect's last keystroke didn't make it into the answer
    // due to a focus-change race.
    if (optionId === G37_OPTION_EDIT) {
      const buffered = (manualText[d.id] ?? '').trim()
      if (buffered) return { ...d, answer: `edit:${buffered}`, status: 'edited' }
    }
    if (optionId === G37_OPTION_DEFER) {
      const buffered = (manualText[d.id] ?? '').trim()
      if (buffered) {
        return {
          ...d,
          answer: `defer:${buffered}`,
          status: 'skipped',
          skip_reason: buffered,
        }
      }
    }
    if (optionId === G37_OPTION_REJECT) {
      const buffered = (manualText[d.id] ?? '').trim()
      if (buffered) {
        return {
          ...d,
          answer: `reject:${buffered}`,
          status: 'rejected',
          rejection_reason: buffered,
        }
      }
    }
    if (optionId === G37_OPTION_PROMOTE) {
      const buffered = (manualText[d.id] ?? '').trim()
      if (buffered) {
        return {
          ...d,
          answer: `promote_to_du:${buffered}`,
          status: 'skipped',
          skip_reason: buffered,
        }
      }
    }
    return d
  })
}
